import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  supabase,
  listMedicationReminders,
  addMedicationReminder,
  removeMedicationReminder,
  reactivateAllMedicationReminders,
  MedicationReminder,
  MedicationLabel,
  Ampm,
} from '../../lib/supabase';

interface Props {
  isOpen: boolean;
  seniorId: string;
  seniorName: string;
  caregiverId: string;
  onClose: () => void;
}

/**
 * F06 brief — caregiver's "Medication Reminders" panel.
 *
 *   - Time picker: 12h format + AM/PM dropdown
 *   - Label dropdown: morning / noon / afternoon / evening (LOCKED — no free text)
 *   - Max 8 active reminders per senior (enforced UI + DB trigger)
 *   - List of active reminders with remove button
 *   - Disclaimer: "AidFone reminders are a memory aid only..."
 *   - Senior-paused banner with "Re-enable reminders" button when active rows
 *     have all flipped to active=false via voice opt-out
 *
 * Loi 25: zero medication names, brands, or dosages ever appear in this UI
 * or in the DB.
 */
const LABELS: MedicationLabel[] = ['morning', 'noon', 'afternoon', 'evening'];
const MAX_ACTIVE = 8;

export const MedicationRemindersDrawer: FC<Props> = ({
  isOpen, seniorId, seniorName, caregiverId, onClose,
}) => {
  const { t } = useTranslation();
  const labelText = (l: MedicationLabel) => t(`medicationReminders.labels.${l}`);
  const [reminders, setReminders] = useState<MedicationReminder[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Ref-based in-flight guard so back-to-back clicks before setSaving(true)
  // flushes don't both pass the local atCap check and race past the DB
  // trigger (one INSERT lands, second 4xx's, UI shows "max 8" while
  // actually at 8/8 — confusing).
  const addInFlightRef = useRef(false);

  // Add-form state
  const [hour, setHour] = useState<number>(8);
  const [minute, setMinute] = useState<number>(0);
  const [ampm, setAmpm] = useState<Ampm>('AM');
  const [label, setLabel] = useState<MedicationLabel>('morning');

  const activeCount = useMemo(() => reminders.filter(r => r.active).length, [reminders]);
  const allPaused = reminders.length > 0 && activeCount === 0;
  const atCap = activeCount >= MAX_ACTIVE;

  const [loadFailed, setLoadFailed] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setLoadFailed(false);
    listMedicationReminders(seniorId).then((rows) => {
      if (!cancelled) {
        setReminders(rows);
        setLoading(false);
      }
    }).catch((e) => {
      if (!cancelled) {
        console.error('[MedRemindersDrawer] load failed', e);
        // loadFailed gates the form + list so we never let the caregiver
        // add a "9th" reminder against an empty-because-failed list and
        // surface the existing rows as "No reminders yet".
        setError(t('medicationReminders.errors.loadFailed'));
        setLoadFailed(true);
        setLoading(false);
      }
    });
    return () => { cancelled = true; };
  }, [isOpen, seniorId, reloadTick]);

  useEffect(() => {
    if (isOpen) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  // Realtime — subscribe to medication_reminders changes for this senior
  // while the drawer is open. Catches:
  //   - caregiver edits in another tab (multi-device caregiver case)
  //   - senior voice-pause flipping active=false on every row (banner appears live)
  //   - DB trigger rejections that another tab observed (we'd see the row never appear)
  // RLS gates the channel; only rows for seniors the caregiver owns are sent.
  useEffect(() => {
    if (!isOpen || !seniorId) return;
    const channel = supabase
      .channel(`med-reminders-${seniorId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'medication_reminders', filter: `senior_user_id=eq.${seniorId}` },
        (payload) => {
          if (payload.eventType === 'INSERT') {
            const row = payload.new as MedicationReminder;
            setReminders((prev) => {
              if (prev.some(r => r.id === row.id)) return prev;
              return [...prev, row].sort(sortByTime);
            });
          } else if (payload.eventType === 'UPDATE') {
            const row = payload.new as MedicationReminder;
            setReminders((prev) => prev.map(r => r.id === row.id ? row : r).sort(sortByTime));
          } else if (payload.eventType === 'DELETE') {
            const old = payload.old as { id?: string };
            if (old.id) {
              setReminders((prev) => prev.filter(r => r.id !== old.id));
            }
          }
        },
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [isOpen, seniorId]);

  const handleAdd = async () => {
    if (addInFlightRef.current) return;            // double-click guard
    if (atCap) {
      setError(t('medicationReminders.errors.atCap', { max: MAX_ACTIVE }));
      return;
    }
    if (!seniorId || !caregiverId) {
      setError(t('medicationReminders.errors.setupNotReady'));
      return;
    }
    addInFlightRef.current = true;
    setSaving(true);
    setError(null);
    try {
      const row = await addMedicationReminder({
        seniorId, caregiverId, hour, minute, ampm, label,
      });
      setReminders((prev) => [...prev, row].sort(sortByTime));
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      if (msg.includes('max_8_active')) {
        setError(t('medicationReminders.errors.atCap', { max: MAX_ACTIVE }));
      } else {
        setError(t('medicationReminders.errors.saveFailed'));
      }
    } finally {
      addInFlightRef.current = false;
      setSaving(false);
    }
  };

  const handleRemove = async (id: string) => {
    setRemovingId(id);
    setError(null);
    try {
      await removeMedicationReminder(id);
      setReminders((prev) => prev.filter(r => r.id !== id));
    } catch {
      setError(t('medicationReminders.errors.removeFailed'));
    } finally {
      setRemovingId(null);
    }
  };

  const handleReactivateAll = async () => {
    setSaving(true);
    setError(null);
    try {
      await reactivateAllMedicationReminders(seniorId);
      setReminders((prev) => prev.map(r => ({ ...r, active: true })));
    } catch {
      setError(t('medicationReminders.errors.reactivateFailed'));
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-[1090] bg-brand-text/40 backdrop-blur-sm animate-fadeIn"
        onClick={onClose}
      />
      <div
        className="fixed top-0 right-0 bottom-0 z-[1100] w-full sm:w-[460px] bg-white shadow-2xl
                   flex flex-col animate-slideInRight border-l border-brand-border"
        role="dialog"
        aria-labelledby="med-reminders-title"
      >
        <header className="flex items-center justify-between p-5 border-b border-brand-border shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
              <span className="material-symbols-outlined text-emerald-600" style={{ fontSize: '22px' }}>
                pill
              </span>
            </div>
            <div>
              <h2 id="med-reminders-title" className="font-display text-lg font-bold text-brand-text leading-tight">
                {t('medicationReminders.title')}
              </h2>
              <p className="text-xs text-brand-text-muted mt-0.5">
                {t('medicationReminders.subtitle', { name: seniorName })}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-brand-bg-warm transition-colors"
            aria-label={t('medicationReminders.close')}
          >
            <span className="material-symbols-outlined text-brand-text-muted">close</span>
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          <p className="text-xs text-brand-text-muted">
            {t('medicationReminders.timezoneNote', { name: seniorName })}
          </p>

          {error && (
            <div className="rounded-xl bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-800 flex items-center justify-between gap-3">
              <span>{error}</span>
              {loadFailed && (
                <button
                  onClick={() => setReloadTick((tick) => tick + 1)}
                  className="shrink-0 text-xs font-semibold bg-white border border-red-300 rounded-lg px-3 py-1 hover:bg-red-100"
                >
                  {t('medicationReminders.tryAgain')}
                </button>
              )}
            </div>
          )}

          {allPaused && (
            <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              <div className="font-semibold mb-1">
                {t('medicationReminders.paused.headline', { name: seniorName })}
              </div>
              <p className="text-xs leading-relaxed mb-2">
                {t('medicationReminders.paused.explainer')}
              </p>
              <button
                onClick={handleReactivateAll}
                disabled={saving}
                className="text-xs font-semibold bg-white border border-amber-300 rounded-lg px-3 py-1.5 hover:bg-amber-100 disabled:opacity-50"
              >
                {t('medicationReminders.paused.reenable')}
              </button>
            </div>
          )}

          {loading ? (
            <div className="text-center text-sm text-brand-text-muted py-8">
              {t('medicationReminders.loading')}
            </div>
          ) : loadFailed ? (
            // Suppress the add-form + list when the load failed — without
            // this we'd render "No reminders yet" + an enabled Add button,
            // letting the caregiver push past the 8-cap they can't see.
            null
          ) : (
            <>
              <section className={`rounded-2xl border border-brand-border p-4 space-y-3 bg-white ${atCap ? 'opacity-60' : ''}`}>
                <h3 className="font-semibold text-sm text-brand-text">
                  {t('medicationReminders.addSection.title')}
                </h3>
                {atCap && (
                  <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1.5">
                    {t('medicationReminders.addSection.removeOneFirst')}
                  </p>
                )}
                <fieldset disabled={atCap} className="flex flex-wrap items-end gap-3 border-0 p-0 m-0 min-w-0">
                  <div>
                    <label className="block text-xs text-brand-text-muted mb-1">
                      {t('medicationReminders.addSection.hour')}
                    </label>
                    <select
                      value={hour}
                      onChange={(e) => setHour(Number(e.target.value))}
                      className="border border-brand-border rounded-lg px-2 py-1.5 text-sm tabular-nums"
                    >
                      {Array.from({ length: 12 }, (_, i) => i + 1).map(h => (
                        <option key={h} value={h}>{h}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-brand-text-muted mb-1">
                      {t('medicationReminders.addSection.minute')}
                    </label>
                    <select
                      value={minute}
                      onChange={(e) => setMinute(Number(e.target.value))}
                      className="border border-brand-border rounded-lg px-2 py-1.5 text-sm tabular-nums"
                    >
                      {[0, 15, 30, 45].map(m => (
                        <option key={m} value={m}>{m.toString().padStart(2, '0')}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-brand-text-muted mb-1">
                      {t('medicationReminders.addSection.ampm')}
                    </label>
                    <select
                      value={ampm}
                      onChange={(e) => setAmpm(e.target.value as Ampm)}
                      className="border border-brand-border rounded-lg px-2 py-1.5 text-sm"
                    >
                      <option value="AM">AM</option>
                      <option value="PM">PM</option>
                    </select>
                  </div>
                  <div className="flex-1 min-w-[140px]">
                    <label className="block text-xs text-brand-text-muted mb-1">
                      {t('medicationReminders.addSection.label')}
                    </label>
                    <select
                      value={label}
                      onChange={(e) => setLabel(e.target.value as MedicationLabel)}
                      className="w-full border border-brand-border rounded-lg px-2 py-1.5 text-sm"
                    >
                      {LABELS.map(l => (
                        <option key={l} value={l}>{labelText(l)}</option>
                      ))}
                    </select>
                  </div>
                </fieldset>
                {/* Inexact-alarm delay. The Android app dropped USE_EXACT_ALARM
                    for Play policy (restricted to alarm/calendar apps) and now
                    schedules with setAndAllowWhileIdle, so a reminder can fire
                    up to ~15 min late — never early. The app has no reminder
                    config screen, so this drawer is the caregiver's only chance
                    to learn that. Copy is parity with android PR #12. */}
                <p className="text-[11px] text-brand-text-muted leading-relaxed">
                  {t('medicationReminders.addSection.timingNote')}
                </p>
                <button
                  onClick={handleAdd}
                  disabled={saving || atCap}
                  className="w-full bg-emerald-600 text-white font-semibold py-2 rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {atCap
                    ? t('medicationReminders.addSection.atCapButton', { count: MAX_ACTIVE, max: MAX_ACTIVE })
                    : saving
                      ? t('medicationReminders.addSection.submitting')
                      : t('medicationReminders.addSection.submit')}
                </button>
              </section>

              <section className="space-y-2">
                <h3 className="font-semibold text-sm text-brand-text">
                  {t('medicationReminders.list.title')}{' '}
                  <span className="text-brand-text-muted font-normal">
                    ({t('medicationReminders.list.counter', { count: activeCount, max: MAX_ACTIVE })})
                  </span>
                </h3>
                {reminders.length === 0 ? (
                  <p className="text-sm text-brand-text-muted italic">
                    {t('medicationReminders.list.empty')}
                  </p>
                ) : (
                  <ul className="space-y-2">
                    {reminders.filter(r => r.active).sort(sortByTime).map(r => {
                      const timeStr = `${r.hour}:${r.minute.toString().padStart(2, '0')} ${r.ampm}`;
                      return (
                        <li
                          key={r.id}
                          className="flex items-center justify-between rounded-xl border border-brand-border bg-white px-3 py-2.5"
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="font-mono text-base font-semibold text-brand-text tabular-nums">
                              {timeStr}
                            </div>
                            <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                              {labelText(r.label)}
                            </span>
                          </div>
                          <button
                            onClick={() => handleRemove(r.id)}
                            disabled={removingId === r.id}
                            aria-label={t('medicationReminders.list.removeAria', { label: labelText(r.label), time: timeStr })}
                            className="p-1.5 rounded-lg hover:bg-red-50 text-brand-text-muted hover:text-red-600 transition-colors disabled:opacity-50"
                          >
                            <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>delete</span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </section>

              <p className="text-[11px] text-brand-text-muted leading-relaxed border-t border-brand-border/60 pt-3">
                {t('medicationReminders.disclaimer')}
              </p>
            </>
          )}
        </div>
      </div>
    </>
  );
};

function sortByTime(a: MedicationReminder, b: MedicationReminder): number {
  const a24 = to24(a.hour, a.ampm) * 60 + a.minute;
  const b24 = to24(b.hour, b.ampm) * 60 + b.minute;
  return a24 - b24;
}

function to24(hour: number, ampm: Ampm): number {
  if (ampm === 'AM' && hour === 12) return 0;
  if (ampm === 'PM' && hour !== 12) return hour + 12;
  return hour;
}

export default MedicationRemindersDrawer;
